import React, { useState, useEffect } from "react";
import ClientSettingSaveButton from "./ClientSettingSaveButton";
import "../SharedComponents/Styles/SharedGenericStyles.css";

export default function ClientConfiguration({ clientID, api }) {

  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");

  // Enable Clinics
  const [enableClinics, setEnableClinics] = useState(false);
  const [tempClinicsValue, setTempClinicsValue] = useState("No");
  const [enableClinicsChanged, setEnableClinicsChanged] = useState(false);

  // Report Randomization
  const [reportValue, setReportValue] = useState("0");
  const [originalReportValue, setOriginalReportValue] = useState("0");
  const [reportOptionChanged, setReportOptionChanged] = useState(false);

  // Enable Output Reports  
  const [enableOutputReports, setEnableOutputReports] = useState(false);
  const [tempOutputReportsValue, setTempOutputReportsValue] = useState("No");
  const [enableOutputReportsChanged, setEnableOutputReportsChanged] = useState(false);

  
  useEffect(() => {
    if (!clientID || clientID === "0") {
      setEnableClinics(false);
      setTempClinicsValue("No");
      setReportValue("0");
      setOriginalReportValue("0");
      return;
    }

    setLoading(true);
    setApiError("");

    const GetOPMEnablementSettings = api
      .transact("GetOPMEnablementSettings", { ClientID: clientID })
      .then(r => r.apiResult)
      .then(value => {
        const isEnabled = value === true;
        setEnableClinics(isEnabled);
        setTempClinicsValue(isEnabled ? "Yes" : "No");
        setEnableClinicsChanged(false);
      });

    const GetClientDetails = api
      .transact("ClientDetails", { PartyID: clientID })
      .then(r => r.apiResult)
      .then(result => {
      const RandomiseReports = String(result?.[0]?.RandomiseReports ?? 0);
       const outputReportsValue = result?.[0]?.CaseManaged && result?.[0]?.ReceivesTriage ? "Yes" : "No";
      setReportValue(RandomiseReports);
      setOriginalReportValue(RandomiseReports);
      setReportOptionChanged(false);

      setTempOutputReportsValue(outputReportsValue);
      setEnableOutputReports(outputReportsValue === "Yes");
      setEnableOutputReportsChanged(false);
      
      });



    Promise.all([GetOPMEnablementSettings, GetClientDetails])
      .catch(err => setApiError(err.message || "Failed loading configuration"))
      .finally(() => setLoading(false));
  }, [clientID, api]);



  const onClinicsChange = e => {
    const clinicVal = e.target.value;
    setTempClinicsValue(clinicVal);
    setEnableClinicsChanged(clinicVal !== (enableClinics ? "Yes" : "No"));
  };

  const onReportChange = e => {
    const reportVal = e.target.value;
    setReportValue(reportVal);
    setReportOptionChanged(reportVal !== originalReportValue);
  };

  const onOutputReportsChange = e => {
    const outputReportsVal = e.target.value;
    setTempOutputReportsValue(outputReportsVal);
    setEnableOutputReportsChanged(outputReportsVal !== (enableOutputReports ? "Yes" : "No"));
  };


  const saveClinics = async () => {
    try {
      await api.transact("SaveClientConfiguration", {
        SettingKey: "EnableOPMClinics",
        ClientID: clientID,
        SettingValue: tempClinicsValue
      });

      setEnableClinics(tempClinicsValue === "Yes");
      setEnableClinicsChanged(false);
    } catch (err) {
      setApiError(err.message || "Failed saving clinics");
    }
  };

  const saveReportOption = async () => {
    try {
      await api.transact("SaveClientConfiguration", {
        SettingKey: "EnableReportOption",
        ClientID: clientID,
        SettingValue: reportValue
      });

      setOriginalReportValue(reportValue);
      setReportOptionChanged(false);
    } catch (err) {
      setApiError(err.message || "Failed saving report option");
    }
  };

    const saveEnableOutputReports = async () => {   
    try {
      await api.transact("SaveClientConfiguration", {
        SettingKey: "EnableOutputReports",
        ClientID: clientID,
        SettingValue: tempOutputReportsValue
      });
      setEnableOutputReports(tempOutputReportsValue === "Yes");
      setEnableOutputReportsChanged(false);
    } catch (err) {
      setApiError(err.message || "Failed saving report option");
    }
  };      


  return (
<div className="clientConfigurationDiv">
  <h2 className="clientConfigHeader">Client Configuration</h2>

  {loading && <p>Loading configuration…</p>}

  {clientID && clientID !== "0" ? (
    <div className="clientConfigTable">
      
      {/* ENABLE CLINICS */}
      <div className="configRow">
        <div className="configLabel">Enable Clinics</div>

        <div className="configValue">
          <select value={tempClinicsValue} onChange={onClinicsChange}>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
        </div>
        <div className="configActions">
          <ClientSettingSaveButton
            isChanged={enableClinicsChanged}
            editButtonHandler={saveClinics}
          />
        </div>
      </div>

      {/* REPORT RANDOMIZATION */}
      <div className="configRow">
        <div className="configLabel">Report Randomization</div>

        <div className="configValue">
          <select value={reportValue} onChange={onReportChange}>
            <option value="0">No reports</option>
            <option value="1">Initial Assessment</option>
            <option value="2">Follow-Up</option>
            <option value="3">IA and FU</option>
            <option value="4">Discharge</option>
            <option value="5">DC and IA</option>
            <option value="6">FU and DC</option>
            <option value="7">IA, FU and DC</option>
          </select>
        </div>
        <div className="configActions">
          <ClientSettingSaveButton
            isChanged={reportOptionChanged}
            editButtonHandler={saveReportOption}
          />
        </div>
      </div>

      {/* Enable Output Reports */}
      <div className="configRow">
        <div className="configLabel">Enable Output Reports</div>

        <div className="configValue">
          <select value={tempOutputReportsValue} onChange={onOutputReportsChange}>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
        </div>
        <div className="configActions">
          <ClientSettingSaveButton
            isChanged={enableOutputReportsChanged}
            editButtonHandler={saveEnableOutputReports}
          />
        </div>
      </div>

    </div>
  ) : (
    <p style={{ color: "#777", fontStyle: "italic" }}>
      Please select a client to configure settings.
    </p>
  )}

  {apiError && <p style={{ color: "red" }}>{apiError}</p>}
</div>
  );
}
